// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import paymentService from '../services/paymentService';

const CheckoutPage = () => {
  const [plan, setPlan] = useState('pro');
  const [interval, setInterval] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricePreview, setPricePreview] = useState(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentService.createSubscription(plan, interval);
      // backend returns { status: 'success', data: { authorization_url: '...' } }
      // paymentService.createSubscription returns response.data, so extract accordingly
      const url = res?.data?.authorization_url || res?.authorization_url || res?.data?.data?.authorization_url;
      if (url) {
        // Redirect the user to Paystack checkout
        window.location.href = url;
        return;
      }

      setError('Failed to create subscription link. Please try again.');
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await paymentService.getPlans();
        // res might be { status: 'success', data: [...] } or an array
        const payload = res && res.data ? res.data : res;
        if (!mounted) return;
        if (Array.isArray(payload) && payload.length) {
          const matched = payload.find(p => (p.plan === plan || p.name?.toLowerCase() === plan) && p.interval === interval);
          if (matched) {
            setPricePreview(matched.formatted || matched.price || matched.amount || '$9.99');
            return;
          }
        }
        // fallback
        setPricePreview(plan === 'pro' ? (interval === 'monthly' ? 'KSH 1,100' : 'KSH 9,999') : 'KSH 0');
      } catch (err) {
        if (!mounted) return;
        setPricePreview(plan === 'pro' ? (interval === 'monthly' ? 'KSH 1,100' : 'KSH 9,999') : 'KSH 0');
      }
    })();
    return () => { mounted = false; };
  }, [plan, interval]);

  return (
    <div>
      <Header />
      <main className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-bold mb-6">Upgrade to Pro</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature comparison */}
          <section className="md:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Choose the plan that fits you</h2>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 border rounded-lg p-4">
                <h3 className="font-bold text-lg">Basic</h3>
                <p className="text-sm text-gray-600 mb-4">Free tier for getting started</p>
                <ul className="space-y-2 text-sm">
                  <li>• 10 notes storage</li>
                  <li>• Basic AI searches (limited)</li>
                  <li>• Community support</li>
                </ul>
              </div>

              <div className="flex-1 border-2 border-purple-500 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/10">
                <h3 className="font-bold text-lg">Pro</h3>
                <p className="text-sm text-gray-600 mb-4">Unlock all features for power users</p>
                <ul className="space-y-2 text-sm">
                  <li>✔ Unlimited notes</li>
                  <li>✔ Priority AI searches</li>
                  <li>✔ Export to PDF & flashcards</li>
                  <li>✔ Priority email support</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment panel */}
          <aside className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium">Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full p-2 border rounded">
                <option value="pro">Pro</option>
                <option value="team">Team</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium">Billing Interval</label>
              <select value={interval} onChange={(e) => setInterval(e.target.value)} className="w-full p-2 border rounded">
                <option value="monthly">Monthly</option>
                
              </select>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-600">Price Preview</div>
              <div className="text-2xl font-bold">{pricePreview || (plan === 'pro' ? 'KSH 1,100' : 'KSH 0')} <span className="text-sm font-medium">/ {interval}</span></div>
            </div>

            {error && <div className="text-red-600 mb-4">{error}</div>}

            <button onClick={handleSubscribe} disabled={loading} className="w-full px-6 py-3 bg-purple-600 text-white rounded font-semibold">
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </button>

            <p className="mt-4 text-xs text-gray-500">You will be redirected to Paystack to complete the purchase.</p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutPage;